import React from "react";
import { Link } from "react-router-dom";
import { PageSEO } from "@/components/PageSEO";
import { CaroselloStudio } from "@/components/CaroselloStudio";
import { SEO } from "@/lib/seo";
import { ArrowRight, CheckCircle, Calculator, BarChart3, LayoutDashboard } from "lucide-react";

/* Testi approvati dalla committente (09/10/2026): non riscrivere o accorciare. */

const SERVIZI = [
  {
    id: "contabilita",
    icon: Calculator,
    etichetta: "SERVIZIO 1",
    h2: "Contabilità e pianificazione fiscale",
    claim: "La contabilità che non si limita a registrare il passato.",
    testo: [
      "Tenere correttamente la contabilità è fondamentale.",
      "Ma per noi non è sufficiente.",
      "La contabilità deve essere aggiornata e diventare uno strumento che ti aiuta a conoscere l'andamento della tua azienda durante l'anno.",
      "Per questo il nostro servizio comprende:"
    ],
    punti: [
      "Contabilità aggiornata mensilmente.",
      "Gestione degli adempimenti contabili e fiscali.",
      "Bilanci e dichiarazioni.",
      "Analisi trimestrale dell'andamento economico, attraverso consulenza e/o report.",
      "Pianificazione fiscale periodica."
    ],
    chiusura: [
      "Non devi aspettare la chiusura dell'esercizio per conoscere i tuoi risultati.",
      "E non devi arrivare impreparato alle scadenze fiscali.",
      "Conosci i tuoi numeri mentre sei ancora in tempo per utilizzarli."
    ],
    cta: { label: "PARLIAMO DELLA TUA CONTABILITÀ", to: "/contatti" }
  },
  {
    id: "controllo-di-gestione",
    icon: BarChart3,
    etichetta: "SERVIZIO 2",
    h2: "Controllo di gestione e consulenza direzionale",
    claim: "Non guardiamo soltanto quanto guadagni. Scopriamo come si forma il risultato.",
    testo: [
      "Il fatturato cresce, ma l'utile non aumenta?",
      "Hai tanti clienti, ma non sai quali attività siano davvero redditizie?",
      "Vorresti investire, assumere o espanderti, ma non conosci l'impatto economico e finanziario delle tue decisioni?",
      "Il controllo di gestione serve proprio a questo.",
      "Attraverso una contabilità gestionale e analisi più approfondite possiamo esaminare:"
    ],
    punti: [
      "Andamento e composizione del fatturato.",
      "Costi e loro incidenza sui ricavi.",
      "Margini di prodotti, servizi o attività.",
      "Flussi finanziari e liquidità.",
      "Indicatori di performance.",
      "Scostamenti rispetto agli obiettivi e alle previsioni."
    ],
    chiusura: [
      "Non ci limitiamo a produrre report.",
      "Analizziamo insieme le informazioni per individuare criticità, opportunità e possibili interventi.",
      "Perché conoscere i numeri è importante. Sapere cosa farne lo è ancora di più.",
      "Il servizio può essere valutato anche per imprese che mantengono il proprio commercialista, quando sono disponibili dati adeguati."
    ],
    cta: { label: "SCOPRI IL CONTROLLO DI GESTIONE", to: "/controllo-di-gestione-micro-piccole-imprese" }
  },
  {
    id: "pannello-direzionale",
    icon: LayoutDashboard,
    etichetta: "SERVIZIO 3",
    h2: "Pannello Direzionale",
    claim: "I numeri della tua azienda. Tutti in un unico cruscotto.",
    testo: [
      "Quanto hai fatturato?",
      "Come stanno andando i risultati?",
      "Quali indicatori meritano attenzione?",
      "Con il Pannello Direzionale puoi consultare le informazioni della tua impresa in un cruscotto personale, organizzato per rendere più semplice la lettura dell'andamento aziendale.",
      "Il servizio prevede:"
    ],
    punti: [
      "Un cruscotto dedicato alla tua impresa.",
      "Aggiornamento mensile.",
      "Indicatori e rappresentazioni grafiche dei dati disponibili.",
      "Una consultazione semplice e organizzata delle informazioni aziendali."
    ],
    chiusura: [
      "Il Pannello Direzionale può essere attivato anche autonomamente, senza affidare necessariamente la contabilità al nostro studio.",
      "In questo caso, l'aggiornamento dipende dalla disponibilità e dalla qualità dei dati forniti.",
      "Gli indicatori presenti vengono definiti in base alle informazioni disponibili e al servizio concordato.",
      "Non devi cercare i numeri tra bilanci, report e fogli di calcolo. Li trovi organizzati in un unico posto."
    ],
    cta: { label: "SCOPRI IL PANNELLO DIREZIONALE", to: "/pannello-direzionale" }
  }
];

const ServiziPage = () => (
  <main className="pt-20" data-testid="servizi-page">
    <PageSEO {...SEO.servizi} />

    <section className="py-16 md:py-24 bg-[#FAF8F5]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="text-gold font-bold tracking-widest text-sm mb-5">I NOSTRI SERVIZI</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] leading-tight mb-8">
          NON SOLTANTO CONTABILITÀ.<br /><span className="text-gold">NUMERI AGGIORNATI, ANALISI E DECISIONI MIGLIORI.</span>
        </h1>
        {[
          "Nel nostro studio, alla gestione contabile e fiscale affianchiamo numeri aggiornati, analisi periodiche e pianificazione fiscale.",
          "Perché conoscere l'andamento della tua azienda durante l'anno ti aiuta a prendere decisioni più consapevoli.",
          "Non vogliamo che tu scopra a fine anno se hai guadagnato, se i costi sono aumentati troppo o se le imposte saranno più alte del previsto.",
          "Vogliamo che tu abbia informazioni utili durante l'anno, quando puoi ancora organizzarti e prendere decisioni.",
          "E se hai bisogno di andare oltre i risultati contabili, possiamo approfondire fatturato, margini, costi, flussi finanziari e indicatori gestionali."
        ].map((t) => (
          <p key={t} className="text-[#3B4658] leading-relaxed mb-4">{t}</p>
        ))}
        <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug border-l-4 border-gold pl-6 mt-8">
          Tre servizi, un unico obiettivo: aiutarti a conoscere meglio la tua azienda e gestirla con maggiore consapevolezza.
        </p>
        <div className="mt-10">
          <CaroselloStudio testid="servizi-carosello-studio" />
        </div>
      </div>
    </section>

    {SERVIZI.map((s, i) => (
      <section
        key={s.id}
        id={s.id}
        className={`py-16 md:py-20 ${i % 2 === 0 ? 'bg-[#F4EFEA]' : 'bg-[#FAF8F5]'}`}
        data-testid={`servizio-${s.id}`}
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <s.icon className="text-gold" size={26} />
            <p className="text-gold text-xs tracking-[0.3em] font-bold">{s.etichetta}</p>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] leading-tight mb-3">{s.h2}</h2>
          <p className="font-heading text-xl md:text-2xl text-gold leading-snug mb-7">{s.claim}</p>
          {s.testo.map((t) => (
            <p key={t} className="text-[#3B4658] leading-relaxed mb-4">{t}</p>
          ))}
          <ul className="space-y-3 my-6">
            {s.punti.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                <span className="text-[#3B4658] leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          {s.chiusura.map((t) => (
            <p key={t} className="text-[#3B4658] leading-relaxed mb-4">{t}</p>
          ))}
          <Link
            to={s.cta.to}
            className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-7 py-4 rounded font-bold tracking-wide transition-all hover:scale-105 mt-4"
            data-testid={`servizio-cta-${s.id}`}
          >
            {s.cta.label} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    ))}

    <section className="py-16 md:py-24 bg-[#0D1527]" data-testid="servizi-da-dove-cominciamo">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-8">Da dove cominciamo?</h2>
        {[
          "Ogni azienda ha esigenze diverse.",
          "C'è chi desidera una contabilità aggiornata e una pianificazione fiscale più attenta.",
          "Chi vuole comprendere nel dettaglio margini, costi e liquidità.",
          "E chi ha bisogno di consultare periodicamente i propri indicatori in un cruscotto.",
          "Non devi scegliere da solo quale servizio sia più adatto.",
          "Raccontaci la tua azienda e capiremo insieme da dove iniziare."
        ].map((t) => (
          <p key={t} className="text-white/75 leading-relaxed mb-4">{t}</p>
        ))}
        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105 mt-6"
          data-testid="servizi-cta-finale"
        >
          PARLIAMO DELLA TUA AZIENDA <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </main>
);

export { ServiziPage };
